import React from 'react';
import { 
  FileEdit, Clock, UserCheck, MessageSquare, CheckCircle, Calendar, 
  CheckSquare, Package, Truck, CircleCheck, CreditCard, BadgeCheck,
  CheckCircle2, Ban, XCircle, Loader
} from 'lucide-react';
import { STATUS_CONFIG } from '../constants/statuses';

// Map des noms d'icônes vers les composants Lucide
const ICON_MAP = {
  FileEdit,
  Clock,
  UserCheck,
  MessageSquare,
  CheckCircle,
  Calendar,
  CheckSquare,
  Package,
  Truck,
  CircleCheck,
  CreditCard,
  BadgeCheck,
  CheckCircle2,
  Ban,
  XCircle,
  Loader,
};

/**
 * StatusBadge - Badge de statut professionnel avec icône Lucide
 * @param {string} status - Le code du statut (ex: 'livree', 'planifiee')
 * @param {boolean} showIcon - Afficher l'icône (default: true)
 * @param {boolean} showLabel - Afficher le label (default: true)
 * @param {string} size - Taille: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} variant - Variante: 'default', 'outline', 'dot' (default: 'default')
 */
export const StatusBadge = ({ 
  status, 
  showIcon = true, 
  showLabel = true,
  size = 'md',
  variant = 'default'
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const IconComponent = ICON_MAP[config.iconName];
  
  // Classes de taille
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  // Variante "dot" - point coloré simple
  if (variant === 'dot') {
    return (
      <span className={`inline-flex items-center gap-1.5 ${sizeClasses[size]}`}>
        <span className={`w-2 h-2 rounded-full bg-${config.color}-500`} />
        {showLabel && <span className={`font-medium text-gray-700`}>{config.shortLabel}</span>}
      </span>
    );
  }

  // Variante "outline" - bordure seulement
  if (variant === 'outline') {
    return (
      <span className={`inline-flex items-center gap-1.5 ${sizeClasses[size]} rounded-full border border-${config.color}-300 ${config.textColor}`}>
        {showIcon && IconComponent && <IconComponent className={iconSizes[size]} />}
        {showLabel && <span className="font-medium">{config.shortLabel}</span>}
      </span>
    );
  }

  // Variante par défaut - badge coloré
  return (
    <span className={`inline-flex items-center gap-1.5 ${sizeClasses[size]} rounded-full ${config.bgColor} ${config.textColor}`}>
      {showIcon && IconComponent && <IconComponent className={iconSizes[size]} />}
      {showLabel && <span className="font-medium">{config.shortLabel}</span>}
    </span>
  );
};

/**
 * StatusIcon - Icône de statut seule
 */
export const StatusIcon = ({ status, size = 'md', className = '' }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const IconComponent = ICON_MAP[config.iconName];
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };
  
  if (!IconComponent) return null;
  
  return <IconComponent className={`${iconSizes[size]} ${config.textColor} ${className}`} />;
};

/**
 * StatusDot - Simple point coloré pour les listes compactes
 */
export const StatusDot = ({ status, size = 'md' }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  
  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5'
  };
  
  return (
    <span 
      className={`inline-block ${dotSizes[size]} rounded-full`}
      style={{ backgroundColor: `var(--${config.color}-500, currentColor)` }}
    />
  );
};

export default StatusBadge;
